// eslint-disable-next-line no-use-before-define
import * as React from 'react';
import cx from 'classnames';
import { Table, Tooltip, Divider, Button, Pagination } from 'antd';
import { Loading3QuartersOutlined } from '@ant-design/icons';

import Error from 'components/Error';
import { getLangKey } from 'models/Language';
import HelpArticleButton from 'utils/HelpArticleButton';

import EmptyState from '../EmptyState';
import Loader from '../Loader';
import {
  onUserTasksTableRow,
  getUserTasksTableColumnsConfig
} from './userTasksTableConfig';
import FilterTask from '../FilterTask';
import { useState } from './userTasksState';
import { isNewDashboardSearchEnabled } from './newDashboardSearch';
import DashboardTaskSearchShell from './DashboardTaskSearchShell';
import { countActiveDashboardTaskSearchFilters } from './dashboardTaskSearchState';
import styles from '../index.module.css';
import * as translations from '../strings';
import { UserTasksProps } from '../types';
import { TaskDetailsDrawer } from './TaskDetailsDrawer';

const strings = translations[getLangKey()];
const getPopupContainer = () =>
  document.getElementById('ReactApp') as HTMLElement;
const bugherdUrl = window.location.origin;
const DASHBOARD_TASK_DETAILS_EXPERIMENT = 'dashboard_task_details';

const UserTasks = ({
  organization,
  config,
  onPusherUpdate,
  refresh,
  email,
  currentUserId,
  currentUserName,
  currentUserRole,
  isManager
}: UserTasksProps) => {
  const organizationId = organization.id;
  const dashboardSearchEnabled = isNewDashboardSearchEnabled();
  const taskDetailsDrawerEnabled = !!organization.experiments?.includes(
    DASHBOARD_TASK_DETAILS_EXPERIMENT
  );

  const {
    isAllTasks,
    setIsAllTasks,
    descriptionSearchValue,
    setDescriptionSearchValue,
    submitDescriptionSearch,
    previewedTaskInfo,
    setPreviewedTaskInfo,
    pageNumber,
    pusherUpdated,
    userTasks,
    userTasksPageSize,
    taskOrderBy,
    dashboardSearchState,
    dashboardSearchPagination,
    setDashboardSearchFilters,
    clearDashboardSearch,
    handleTableChange,
    handlePagination,
    userTaskFilter: filter,
    setUserTaskFilter,
    getUserTask: { previewedTask, taskLoading, refetchTask, isSwitchingTask },
    getUserTasks: { data, loading, error, refetch }
  } = useState({
    organizationId,
    currentUserId,
    config,
    dashboardSearchEnabled
  });

  if (refresh && !dashboardSearchEnabled) refetch();

  React.useEffect(() => {
    if (refresh && dashboardSearchEnabled) refetch();
  }, [dashboardSearchEnabled, refresh, refetch]);

  React.useEffect(() => {
    if (pusherUpdated) onPusherUpdate();
  }, [pusherUpdated]);

  if (loading && userTasks.length === 0 && !dashboardSearchEnabled) {
    return <Loader />;
  }
  if (error) return <Error error={error} />;
  if (!data && userTasks.length === 0 && !dashboardSearchEnabled) {
    return <p>Not found</p>;
  }

  const committedSearchQuery = dashboardSearchEnabled
    ? dashboardSearchState.query
    : descriptionSearchValue;
  const hasActiveDashboardSearch =
    dashboardSearchEnabled &&
    (!!committedSearchQuery.trim() ||
      countActiveDashboardTaskSearchFilters(dashboardSearchState.filters) > 0);
  const columns = getUserTasksTableColumnsConfig({
    descriptionSearchValue: committedSearchQuery,
    pageSearchValue: dashboardSearchState.filters.page,
    showSearchMatchContext: hasActiveDashboardSearch,
    taskOrderBy: dashboardSearchEnabled ? taskOrderBy : undefined
  });
  const searchLoading = dashboardSearchEnabled && loading;
  const searchLoadingRowCount = Math.min(Math.max(userTasks.length, 4), 8);
  const previewIndex = previewedTaskInfo.id
    ? userTasks.findIndex(task => task.id === previewedTaskInfo.id)
    : -1;
  const isPreviewingFirstTask = previewIndex === 0;

  const emptyUserTasks =
    data && data.currentUser.userTasks.length === 0 && !searchLoading;
  const showFilterPanel: boolean = !emptyUserTasks || !!filter;
  const dashboardSearchCurrentUser = {
    id: Number(currentUserId),
    name: currentUserName,
    email,
    role: currentUserRole === 'guest' ? 'guest' : undefined
  };

  /* can only guarantee there's no more tasks if the current page has less than the page size */
  const hasMoreTasks =
    data && data.currentUser.userTasks.length >= userTasksPageSize;

  const hasTasksButNoneOnThisPage = pageNumber > 1 && emptyUserTasks;

  const renderEmptyState = () => {
    if (hasTasksButNoneOnThisPage) {
      return (
        <EmptyState
          title={strings.emptyPageTasksTitle}
          help={strings.emptyPageTasksHelp}
          container="Task"
        />
      );
    }

    if (hasActiveDashboardSearch || (!dashboardSearchEnabled && filter)) {
      return (
        <EmptyState
          title={strings.emptyNoSearchTitle}
          help={strings.emptyNoSearchHelp}
          container="Task"
        />
      );
    }

    return (
      <EmptyState
        title={strings.emptyUserTasksTitle}
        help={strings.emptyUserTasksHelp}
        container="Task"
      />
    );
  };

  return (
    <>
      <h2 className={styles.myDashboard}>{strings.dashboard}</h2>
      <h4 className={styles.myTasksHeader}>
        <span className={styles.tasksRadioWrapper}>
          <Tooltip
            title={<p>{strings.myTasksInfoIcon}</p>}
            getPopupContainer={getPopupContainer}
            placement="rightBottom"
          >
            <span
              className={cx(styles.taskHeaderOuter, {
                [styles.activeTaskHeaderOuter]: !isAllTasks
              })}
              onClick={() => setIsAllTasks(false)}
            >
              {strings.userTasksHeader}
            </span>
          </Tooltip>
          <Tooltip
            title={
              <p>
                {strings[isManager ? 'allTasksInfoIcon' : 'allMemberTasks']}
              </p>
            }
            getPopupContainer={getPopupContainer}
            placement="rightBottom"
          >
            <span
              className={cx(styles.taskHeaderOuter, styles.allTasksHeader, {
                [styles.activeTaskHeaderOuter]: isAllTasks
              })}
              onClick={() => setIsAllTasks(true)}
            >
              {strings.allTasksHeader}
            </span>
          </Tooltip>
        </span>
        <span className={styles.helpIconOuter}>
          <HelpArticleButton articleId={strings.myTaskInfoHelpArticleId} />
        </span>
      </h4>
      <Divider className={styles.divider} />
      <div />
      {dashboardSearchEnabled ? (
        <DashboardTaskSearchShell
          committedQuery={dashboardSearchState.query}
          currentUser={dashboardSearchCurrentUser}
          filters={dashboardSearchState.filters}
          onClear={clearDashboardSearch}
          onFiltersChange={setDashboardSearchFilters}
          onQueryChange={setDescriptionSearchValue}
          onQuerySubmit={submitDescriptionSearch}
          organizationId={organizationId}
          pagination={dashboardSearchPagination}
          query={descriptionSearchValue}
          statusNames={organization.statusNames || []}
          tagNames={organization.tagNames || []}
        />
      ) : (
        showFilterPanel && (
          <FilterTask
            className={styles.filterMargin}
            onFilter={setUserTaskFilter}
            defaultFilter={filter}
            organization={organization}
            searchValue={descriptionSearchValue}
            onSearch={setDescriptionSearchValue}
            showExport={
              !emptyUserTasks && isAllTasks && !!organization.bulkExport
            }
            currentUserEmail={email}
            exportProps={{
              organizationId,
              isAllTasks,
              orderBy: taskOrderBy
            }}
          />
        )
      )}
      {emptyUserTasks ? (
        <div className={styles.emptyUserTasks}>{renderEmptyState()}</div>
      ) : (
        <Table
          className={cx(styles.table)}
          rowKey="id"
          onRow={userTask =>
            onUserTasksTableRow({
              userTask,
              previewedTaskInfo,
              setPreviewedTaskInfo,
              taskDetailsDrawerEnabled
            })
          }
          dataSource={searchLoading ? [] : userTasks}
          loading={
            searchLoading && {
              spinning: true,
              indicator: (
                <Loading3QuartersOutlined style={{ fontSize: 36 }} spin />
              )
            }
          }
          locale={
            searchLoading
              ? {
                  emptyText: (
                    <div
                      className={styles.searchLoadingRows}
                      role="status"
                      aria-label={strings.searchingTasks}
                    >
                      {Array.from(
                        { length: searchLoadingRowCount },
                        (_, index) => (
                          <div
                            key={index}
                            className={styles.searchLoadingRow}
                            aria-hidden="true"
                          />
                        )
                      )}
                    </div>
                  )
                }
              : undefined
          }
          pagination={false}
          columns={columns}
          onChange={handleTableChange}
          showSorterTooltip={false}
        />
      )}
      {taskDetailsDrawerEnabled && previewedTask && (
        <TaskDetailsDrawer
          task={previewedTask}
          taskLoading={taskLoading}
          isSwitchingTask={isSwitchingTask}
          bugherdUrl={bugherdUrl}
          getPopupContainer={getPopupContainer}
          onClose={() =>
            setPreviewedTaskInfo({ id: undefined, apiKey: undefined })
          }
          refetchTask={refetchTask}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          onOpenTask={taskId => {
            const match = userTasks.find(task => Number(task.id) === taskId);
            if (!match) return;
            setPreviewedTaskInfo({
              id: match.id,
              apiKey: match.project.apiKey
            });
          }}
          showTaskNav={{
            previous: userTasks.length > 1 && !isPreviewingFirstTask,
            next: userTasks.length > 1 && previewIndex !== userTasks.length - 1
          }}
          onTaskChange={(next?: boolean) => {
            const newIndex = previewIndex + (next ? 1 : -1);
            const newTask = userTasks[newIndex];
            if (!newTask) return;
            setPreviewedTaskInfo({
              id: newTask.id,
              apiKey: newTask.project.apiKey
            });
          }}
        />
      )}

      <div className={styles.pagination}>
        {dashboardSearchEnabled ? (
          dashboardSearchPagination &&
          dashboardSearchPagination.totalPages > 1 && (
            <Pagination
              current={dashboardSearchPagination.page}
              disabled={loading}
              onChange={page => handlePagination(page)}
              pageSize={dashboardSearchPagination.perPage}
              showSizeChanger={false}
              total={dashboardSearchPagination.filteredCount}
            />
          )
        ) : (
          <>
            {pageNumber !== 1 && (
              <Button
                type="primary"
                onClick={() => handlePagination(pageNumber - 1)}
              >
                Previous
              </Button>
            )}{' '}
            {hasMoreTasks && (
              <Button
                type="primary"
                onClick={() => handlePagination(pageNumber + 1)}
              >
                Next
              </Button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UserTasks;
